// OverlayEditor — interaktives Bild-Overlay zum Platzieren/Skalieren/Drehen/Verzerren auf einer Admin-Karte.
//
// Ersetzt das Legacy-Trio aus javascript/overlay.js: ProjectedOverlay + 2 Eckmarker + Center-Marker.
// Verantwortung: nur Darstellung + Interaktion; KEIN Speichern, kein DB-Wissen, kein Tiling.
//
// MODELL (Unified-Quad, 2026-06-17): Das Overlay ist IMMER ein Quad aus 4 Ecken (`_corners`, LatLng) + ein
// Rotationswinkel (`_rotation`). Default-Quad = die achsenparallele Box. Gerendert wird immer per CSS `matrix3d`
// (Rotation in die Eckpositionen eingerechnet) → das Bild, nicht der Div, trägt die Transform; die Griffe sitzen
// per JS an den echten Eckpixeln und bleiben uniform.
//   - Move: verschiebt das ganze Quad.
//   - Resize (Eckgriff): skaliert das Quad gleichmäßig um die gegenüberliegende Ecke.
//   - Rotate: Winkel (2. Klick aufs aktive Overlay = rotate-Modus; Shift=15°-Raster) — auch per Textbox setzbar.
//   - Verzerren (Checkbox = Editiermodus): freie Eck-Griffe. Abhaken behält die verzerrte Form (nicht-exklusiv);
//     sie bleibt skalier-/rotierbar. Reset setzt NUR die Verzerrung zurück (Quad → Box), Rotation bleibt.
// Rotation/Verzerren sind experimentell (in-memory via getParams; noch nicht in DB/GroundOverlay/Tiles).
//
// google wird zur Laufzeit injiziert (wie GoogleMapOverlayFactory), weil OverlayView erst nach API-Load existiert.

import { rotatePoint, matrix3dForQuad } from "../tools/quadTransform.js";

export function createOverlayEditorClass(google) {
    const HANDLE_KEYS = ['nw', 'ne', 'se', 'sw'];
    const HANDLE_CURSOR = { nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize' };
    const EDGE_KEYS = ['n', 'e', 's', 'w'];
    const EDGE_CURSOR = { n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize' };

    // Eigener Rotations-Cursor (Kreispfeil-SVG, schwarz mit weißer Kontur), Fallback `grab`.
    const ROTATE_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='%23000' stroke='%23fff' stroke-width='1.5' d='M15.55 5.55L11 1v3.07C7.06 4.56 4 7.92 4 12s3.05 7.44 7 7.93v-2.02c-2.84-.48-5-2.94-5-5.91s2.16-5.43 5-5.91V10l4.55-4.45zM19.93 11c-.17-1.39-.72-2.73-1.62-3.89l-1.42 1.42c.54.75.88 1.6 1.02 2.47h2.02zM13 17.9v2.02c1.39-.17 2.74-.71 3.9-1.61l-1.44-1.44c-.75.54-1.59.89-2.46 1.03zm3.89-2.42l1.42 1.41c.9-1.16 1.45-2.5 1.62-3.89h-2.02c-.14.87-.48 1.72-1.02 2.48z'/%3E%3C/svg%3E";
    const ROTATE_CURSOR = 'url("' + ROTATE_SVG + '") 12 12, grab';
    // Aktives Overlay liegt immer ganz vorn (klick-/ziehbar bei Überlappung); inaktive folgen ihrer Layer-Reihenfolge (_zIndex).
    const ACTIVE_Z_INDEX = 100000;

    return class OverlayEditor extends google.maps.OverlayView {
        constructor(map, imageUrl, bounds, opts = {}) {
            super();
            this._map = map;
            this._url = imageUrl;
            this._opacityPct = opts.percentOpacity ?? opts.opacity ?? 50;
            this._onChange = typeof opts.onChange === 'function' ? opts.onChange : null;
            this._onSelect = typeof opts.onSelect === 'function' ? opts.onSelect : null;
            this._onRotateChange = typeof opts.onRotateChange === 'function' ? opts.onRotateChange : null;
            this._onModeChange = typeof opts.onModeChange === 'function' ? opts.onModeChange : null;
            this._onHistoryChange = typeof opts.onHistoryChange === 'function' ? opts.onHistoryChange : null;
            // Wird NACH jedem draw() gerufen. Nötig, weil Google die OverlayViews in unbestimmter Reihenfolge
            // zeichnet: die Toolbar hängt an DIESER Geometrie und muss danach dran sein, nicht davor.
            this._onAfterDraw = typeof opts.onAfterDraw === 'function' ? opts.onAfterDraw : null;
            this._editable = opts.editable !== false;
            this._active = opts.active === true;
            this._aspectLock = opts.aspectLock !== false;   // (im Quad-Modell aktuell ohne Wirkung; Scale ist gleichmäßig)
            this._rotation = opts.rotation || 0;             // Grad — in-memory
            this._distortEdit = false;                       // Checkbox „verzerren" = Editiermodus (freie Eckgriffe)
            this._zIndex = 1;                                // Layer-/Stapel-Reihenfolge (vom Host gesetzt; aktiv → ACTIVE_Z_INDEX)

            this._corners = null;                            // {nw,ne,se,sw} LatLng — IMMER gesetzt (Quad)
            this._initCornersFromBounds(bounds);

            this._div = null;
            this._img = null;
            this._handles = [];      // Eck-Griffe
            this._edges = [];        // Kanten-Griffe (N/O/S/W; nur bei unverzerrter Form im Resize-Modus)
            this._distorted = false; // Quad ist kein Rechteck mehr (nach freier Eck-Verschiebung)

            this._drag = null;       // Move
            this._scale = null;      // Resize/Scale
            this._rotate = null;     // Rotate
            this._distortDrag = null;// freie Eck-Verschiebung
            this._edgeDrag = null;   // Kanten-Resize
            this._mode = 'resize';   // 'resize' | 'rotate' (Toggle bei 2. Klick aufs aktive Overlay; nicht im Verzerren-Editiermodus)
            this._moved = false;
            this._downActive = false;

            this._boundMove = (e) => this._onMove(e);
            this._boundEnd = (e) => this._endMove(e);
            this._boundScale = (e) => this._onScale(e);
            this._boundScaleEnd = () => this._endScale();
            this._boundRotate = (e) => this._onRotate(e);
            this._boundRotateEnd = () => this._endRotate();
            this._boundDistort = (e) => this._onDistort(e);
            this._boundDistortEnd = () => this._endDistort();
            this._boundEdge = (e) => this._onEdge(e);
            this._boundEdgeEnd = () => this._endEdge();

            // Persistierte Verzerrung anwenden: Quad aus den gespeicherten (un-rotierten) Ecken setzen.
            // Rotation bleibt separat (opts.rotation, oben) → Render rechnet sie auf die Ecken.
            if (opts.distort && opts.distort.nw && opts.distort.ne && opts.distort.se && opts.distort.sw) {
                this._setCornersFromDistort(opts.distort);
            }

            // Chef 17: Form-History (Snapshots aus _corners + _rotation + _distorted). Initialer Snapshot = Startform.
            this._history = [];
            this._historyIdx = -1;
            this._pushHistory();

            this.setMap(map);
        }

        onAdd() {
            const div = document.createElement('div');
            div.style.position = 'absolute';

            // Verzerr-Wrapper: trägt die matrix3d-Transform (siehe draw()) → Bild UND der 10%-Weiß-BG
            // warpen GEMEINSAM mit. Der BG liegt auf dem Wrapper (nicht dem img) → opacity-unabhängig
            // (setOpacity dimmt nur das img) → Hinweis auf transparente PNGs, auch bei Overlay-Opacity 0.
            const warp = document.createElement('div');
            warp.style.position = 'absolute';
            warp.style.width = '100%';
            warp.style.height = '100%';
            warp.style.transformOrigin = '0 0';
            warp.style.background = 'rgba(255, 255, 255, 0.1)';

            const img = document.createElement('img');
            img.src = this._url;
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.display = 'block';
            img.draggable = false;

            warp.appendChild(img);
            div.appendChild(warp);
            this._div = div;
            this._warp = warp;
            this._img = img;
            this.setOpacity(this._opacityPct);

            this.getPanes().overlayMouseTarget.appendChild(div);

            if (this._editable) {
                img.style.cursor = 'move';
                img.addEventListener('mousedown', (e) => this._beginMove(e));
                // Klick/Dblklick nicht an die Karte durchreichen (sonst Deselect-Race / Map-Zoom).
                div.addEventListener('click', (e) => e.stopPropagation());
                div.addEventListener('dblclick', (e) => { e.stopPropagation(); e.preventDefault(); });
                this._addHandles(div);
                this._applyMode();
            }
            this._applyActive();
            if (this._hidden) { div.style.visibility = 'hidden'; }   // hidden state survives a re-add (setMap)
        }

        _addHandles(div) {
            // Per JS positioniert (left/top) + margin-zentriert; NICHT transformiert → bleiben uniform.
            for (const key of HANDLE_KEYS) {
                const h = document.createElement('div');
                h.style.position = 'absolute';
                h.style.width = '12px';
                h.style.height = '12px';
                h.style.marginLeft = '-6px';
                h.style.marginTop = '-6px';
                h.style.boxSizing = 'border-box';
                h.style.background = 'var(--tc-main, #c356a0)';
                h.style.border = '2px solid #fff';
                h.style.cursor = HANDLE_CURSOR[key];
                h.style.zIndex = '2';
                h.addEventListener('mousedown', (e) => this._beginHandle(e, key));
                div.appendChild(h);
                this._handles.push(h);
            }
            for (const key of EDGE_KEYS) {
                const h = document.createElement('div');
                h.style.position = 'absolute';
                h.style.width = '12px';
                h.style.height = '12px';
                h.style.marginLeft = '-6px';
                h.style.marginTop = '-6px';
                h.style.boxSizing = 'border-box';
                h.style.background = 'var(--tc-main, #c356a0)';
                h.style.border = '2px solid #fff';
                h.style.cursor = EDGE_CURSOR[key];
                h.style.zIndex = '2';
                h.addEventListener('mousedown', (e) => this._beginEdge(e, key));
                div.appendChild(h);
                this._edges.push(h);
            }
        }

        // --- Quad-Geometrie ---

        _initCornersFromBounds(bounds) {
            const ne = bounds.getNorthEast(), sw = bounds.getSouthWest();
            this._corners = {
                nw: new google.maps.LatLng(ne.lat(), sw.lng()),
                ne: new google.maps.LatLng(ne.lat(), ne.lng()),
                se: new google.maps.LatLng(sw.lat(), ne.lng()),
                sw: new google.maps.LatLng(sw.lat(), sw.lng())
            };
            this._distorted = false;
        }

        // Setzt das Quad direkt aus gespeicherten (un-rotierten) Ecken (Persistenz-Load). distort = {nw,ne,se,sw:{lat,lng}}.
        _setCornersFromDistort(distort) {
            this._corners = {
                nw: new google.maps.LatLng(distort.nw.lat, distort.nw.lng),
                ne: new google.maps.LatLng(distort.ne.lat, distort.ne.lng),
                se: new google.maps.LatLng(distort.se.lat, distort.se.lng),
                sw: new google.maps.LatLng(distort.sw.lat, distort.sw.lng)
            };
            this._distorted = true;
        }

        // Liefert die 4 Eck-Pixel unrotiert (up), rotiert um die Mitte (rp) und das Zentrum.
        _cornerPixels() {
            const proj = this.getProjection();
            if (!proj) { return null; }
            const up = {};
            for (const k of HANDLE_KEYS) { const p = proj.fromLatLngToDivPixel(this._corners[k]); up[k] = { x: p.x, y: p.y }; }
            const cx = (up.nw.x + up.ne.x + up.se.x + up.sw.x) / 4;
            const cy = (up.nw.y + up.ne.y + up.se.y + up.sw.y) / 4;
            const center = { x: cx, y: cy };
            const rp = {};
            for (const k of HANDLE_KEYS) { rp[k] = rotatePoint(up[k], center, this._rotation || 0); }
            return { up, rp, center };
        }

        // Bounding-Box der (unrotierten) Quad-Ecken → für getBounds()/Save (Hidden-Inputs).
        getBounds() {
            let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
            for (const k of HANDLE_KEYS) {
                const c = this._corners[k];
                minLat = Math.min(minLat, c.lat()); maxLat = Math.max(maxLat, c.lat());
                minLng = Math.min(minLng, c.lng()); maxLng = Math.max(maxLng, c.lng());
            }
            return new google.maps.LatLngBounds(new google.maps.LatLng(minLat, minLng), new google.maps.LatLng(maxLat, maxLng));
        }

        _emitChange() {
            if (this._onChange) { this._onChange(this.getBounds()); }
        }

        // Div-Pixel-Anker unter der TATSÄCHLICH gerenderten Form (rotiert/verzerrt): Zentroid-x + tiefster Eckpixel.
        // Für UI, die unter dem Overlay sitzen soll (Toolbar) — NICHT die un-rotierte Bounding-Box.
        getRenderedBottomAnchor() {
            const cp = this._cornerPixels();
            if (!cp) { return null; }
            const rp = cp.rp;
            const cx = (rp.nw.x + rp.ne.x + rp.se.x + rp.sw.x) / 4;
            const maxY = Math.max(rp.nw.y, rp.ne.y, rp.se.y, rp.sw.y);
            return { x: cx, y: maxY };
        }

        // Derselbe Anker, aber RELATIV zur eigenen Div-Ecke (wie die Griffe rechnen, s. _positionHandles). Für Anbauten,
        // die als KIND im Editor-Div hängen: der absolute Ursprung kürzt sich raus, damit kann die Position eines solchen
        // Kindes gegenüber dem Bild nicht veralten — egal welchen Stand die Projektion gerade hat.
        getRenderedBottomAnchorLocal() {
            const cp = this._cornerPixels();
            if (!cp) { return null; }
            const rp = cp.rp;
            const left = Math.min(rp.nw.x, rp.ne.x, rp.se.x, rp.sw.x);
            const top = Math.min(rp.nw.y, rp.ne.y, rp.se.y, rp.sw.y);
            const cx = (rp.nw.x + rp.ne.x + rp.se.x + rp.sw.x) / 4;
            const maxY = Math.max(rp.nw.y, rp.ne.y, rp.se.y, rp.sw.y);
            return { x: cx - left, y: maxY - top };
        }

        // Das Wurzel-Element des Editors (Bild + Griffe). Anbauten wie die Toolbar hängen sich hier hinein.
        getElement() { return this._div; }

        // --- Render: Quad (rotiert) per matrix3d aufs Bild; Griffe an die Eckpixel ---

        draw() {
            if (!this._div) { return; }
            const cp = this._cornerPixels();
            if (!cp) { return; }
            const up = cp.up, rp = cp.rp;
            const refW = Math.max(1, Math.max(up.nw.x, up.ne.x, up.se.x, up.sw.x) - Math.min(up.nw.x, up.ne.x, up.se.x, up.sw.x));
            const refH = Math.max(1, Math.max(up.nw.y, up.ne.y, up.se.y, up.sw.y) - Math.min(up.nw.y, up.ne.y, up.se.y, up.sw.y));
            const left = Math.min(rp.nw.x, rp.ne.x, rp.se.x, rp.sw.x);
            const top = Math.min(rp.nw.y, rp.ne.y, rp.se.y, rp.sw.y);
            this._div.style.left = left + 'px';
            this._div.style.top = top + 'px';
            this._div.style.width = refW + 'px';
            this._div.style.height = refH + 'px';
            const rel = {
                nw: { x: rp.nw.x - left, y: rp.nw.y - top },
                ne: { x: rp.ne.x - left, y: rp.ne.y - top },
                sw: { x: rp.sw.x - left, y: rp.sw.y - top },
                se: { x: rp.se.x - left, y: rp.se.y - top }
            };
            if (this._warp) {
                // Transform auf den Wrapper → Bild + 10%-BG warpen gemeinsam (transformOrigin in onAdd gesetzt).
                this._warp.style.transform = matrix3dForQuad(refW, refH, rel);
            }
            this._positionHandles(rel);
            // Abhängige Anbauten (Toolbar) ZULETZT — erst jetzt steht die Geometrie dieses Durchgangs.
            if (this._onAfterDraw) { this._onAfterDraw(); }
        }

        _positionHandles(c) {
            const byIndex = [c.nw, c.ne, c.se, c.sw];   // HANDLE_KEYS: nw, ne, se, sw
            for (let i = 0; i < this._handles.length; i++) {
                this._handles[i].style.left = byIndex[i].x + 'px';
                this._handles[i].style.top = byIndex[i].y + 'px';
            }
            if (this._edges && this._edges.length) {
                const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
                const em = [mid(c.nw, c.ne), mid(c.ne, c.se), mid(c.se, c.sw), mid(c.sw, c.nw)];   // EDGE_KEYS: n,e,s,w
                for (let i = 0; i < this._edges.length; i++) {
                    this._edges[i].style.left = em[i].x + 'px';
                    this._edges[i].style.top = em[i].y + 'px';
                }
                this._updateEdgeVisibility();
            }
        }

        onRemove() {
            this._endMove();
            this._endScale();
            this._endRotate();
            this._endDistort();
            this._endEdge();
            if (this._div && this._div.parentNode) { this._div.parentNode.removeChild(this._div); }
            this._div = null;
            this._img = null;
        }

        // --- Eckgriff-Dispatch nach Modus ---

        _beginHandle(e, key) {
            if (this._onSelect) { this._onSelect(); }
            e.preventDefault();
            e.stopPropagation();
            if (this._distortEdit) { this._beginDistort(e, key); return; }
            if (this._mode === 'rotate') { this._beginRotate(e); return; }
            this._beginScale(e, key);
        }

        // --- Move: ganzes Quad verschieben ---

        _beginMove(e) {
            this._downActive = this._active;
            this._moved = false;
            const cp = this._cornerPixels();
            if (!cp) { return; }
            e.preventDefault();     // kein natives Bild-Ziehen
            e.stopPropagation();
            this._drag = { downX: e.clientX, downY: e.clientY };
            if (this._active) {
                // Aktiv → verschieben: Karte fixieren, Quad mit der Maus bewegen.
                this._drag.mode = 'move';
                this._drag.up = cp.up;
                this._map.setOptions({ draggable: false });
            } else {
                // Inaktiv → NICHT greifen: Drag pannt die Karte (manuell weitergereicht, s. _onMove), Klick
                // ohne Drag wählt aus (s. _endMove). Verhindert versehentliches Verschieben beim Pannen (Chef).
                this._drag.mode = 'pan';
                this._drag.lastX = e.clientX;
                this._drag.lastY = e.clientY;
            }
            document.addEventListener('mousemove', this._boundMove);
            document.addEventListener('mouseup', this._boundEnd);
        }

        _onMove(e) {
            const d = this._drag;
            if (!d) { return; }
            if (!this._moved && (Math.abs(e.clientX - d.downX) > 2 || Math.abs(e.clientY - d.downY) > 2)) {
                this._moved = true;
            }
            if (d.mode === 'move') {
                const proj = this.getProjection();
                const dx = e.clientX - d.downX, dy = e.clientY - d.downY;
                const up = d.up, next = {};
                for (const k of HANDLE_KEYS) { next[k] = proj.fromDivPixelToLatLng(new google.maps.Point(up[k].x + dx, up[k].y + dy)); }
                this._corners = next;
                this.draw();
                this._emitChange();
            } else {
                // pan: overlayMouseTarget-Pane fängt den nativen Karten-Drag ab → manuell nachziehen.
                // INSTANT via Projektion + setCenter statt map.panBy — panBy ist ANIMIERT und ruckelt Move-für-Move.
                const proj = this._map.getProjection();
                if (proj) {
                    const scale = Math.pow(2, this._map.getZoom());
                    const c = proj.fromLatLngToPoint(this._map.getCenter());
                    c.x -= (e.clientX - d.lastX) / scale;   // Screen-Pixel-Delta → Welt-Koordinaten-Verschiebung
                    c.y -= (e.clientY - d.lastY) / scale;
                    this._map.setCenter(proj.fromPointToLatLng(c));
                } else {
                    this._map.panBy(d.lastX - e.clientX, d.lastY - e.clientY);   // Fallback bis Projektion bereit
                }
                d.lastX = e.clientX;
                d.lastY = e.clientY;
            }
        }

        _endMove() {
            const d = this._drag;
            if (this._drag) { this._drag = null; }
            document.removeEventListener('mousemove', this._boundMove);
            document.removeEventListener('mouseup', this._boundEnd);
            if (this._map) { this._map.setOptions({ draggable: true }); }
            if (!d) { return; }
            if (!this._moved) {
                // Klick ohne Drag: war aktiv → Modus toggeln (resize ↔ rotate); war inaktiv → auswählen.
                if (this._downActive) {
                    if (!this._distortEdit) { this._setMode(this._mode === 'resize' ? 'rotate' : 'resize'); }
                } else if (this._onSelect) {
                    this._onSelect();
                }
            } else if (d.mode === 'move') {
                this._pushHistory();   // Chef 17: Move als History-Schritt
            }
        }

        // --- Resize/Scale: Quad gleichmäßig um die gegenüberliegende Ecke skalieren ---

        _beginScale(e, key) {
            const cp = this._cornerPixels();
            if (!cp) { return; }
            const anchorKey = OPPOSITE[key];
            this._scale = {
                up: cp.up,
                anchorUp: cp.up[anchorKey],
                rotAnchor: cp.rp[anchorKey],
                rotDragged: cp.rp[key],
                startMouse: { x: e.clientX, y: e.clientY }
            };
            this._map.setOptions({ draggable: false });
            document.addEventListener('mousemove', this._boundScale);
            document.addEventListener('mouseup', this._boundScaleEnd);
        }

        _onScale(e) {
            if (!this._scale) { return; }
            const s = this._scale;
            const dx = e.clientX - s.startMouse.x, dy = e.clientY - s.startMouse.y;
            const startDist = Math.hypot(s.rotDragged.x - s.rotAnchor.x, s.rotDragged.y - s.rotAnchor.y);
            if (startDist < 1) { return; }
            const curDist = Math.hypot((s.rotDragged.x + dx) - s.rotAnchor.x, (s.rotDragged.y + dy) - s.rotAnchor.y);
            let f = curDist / startDist;
            if (f < 0.05) { f = 0.05; }   // nicht kollabieren
            const proj = this.getProjection();
            const next = {};
            for (const k of HANDLE_KEYS) {
                const nx = s.anchorUp.x + (s.up[k].x - s.anchorUp.x) * f;
                const ny = s.anchorUp.y + (s.up[k].y - s.anchorUp.y) * f;
                next[k] = proj.fromDivPixelToLatLng(new google.maps.Point(nx, ny));
            }
            this._corners = next;
            this.draw();
            this._emitChange();
        }

        _endScale() {
            const was = !!this._scale;
            if (this._scale) { this._scale = null; }
            document.removeEventListener('mousemove', this._boundScale);
            document.removeEventListener('mouseup', this._boundScaleEnd);
            if (this._map) { this._map.setOptions({ draggable: true }); }
            if (was) { this._pushHistory(); }   // Chef 17
        }

        // --- Rotate ---

        _beginRotate(e) {
            const c = this._centerClient();
            this._rotate = { startRotation: this._rotation, startAngle: Math.atan2(e.clientY - c.y, e.clientX - c.x) };
            this._map.setOptions({ draggable: false });
            document.addEventListener('mousemove', this._boundRotate);
            document.addEventListener('mouseup', this._boundRotateEnd);
        }

        _onRotate(e) {
            if (!this._rotate) { return; }
            const c = this._centerClient();
            const angle = Math.atan2(e.clientY - c.y, e.clientX - c.x);
            let deg = this._rotate.startRotation + (angle - this._rotate.startAngle) * 180 / Math.PI;
            if (e.shiftKey) { deg = Math.round(deg / 15) * 15; }
            this.setRotation(deg);
        }

        _endRotate() {
            const was = !!this._rotate;
            if (this._rotate) { this._rotate = null; }
            document.removeEventListener('mousemove', this._boundRotate);
            document.removeEventListener('mouseup', this._boundRotateEnd);
            if (this._map) { this._map.setOptions({ draggable: true }); }
            if (was) { this._pushHistory(); }   // Chef 17
        }

        _centerClient() {
            const r = this._div.getBoundingClientRect();
            return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
        }

        // --- Verzerren (Editiermodus): freie Eck-Verschiebung; in den unrotierten Raum zurückgerechnet ---

        _beginDistort(e, key) {
            const cp = this._cornerPixels();
            if (!cp) { return; }
            this._distortDrag = { key: key, startScreen: cp.rp[key], center: cp.center, startMouse: { x: e.clientX, y: e.clientY } };
            this._map.setOptions({ draggable: false });
            document.addEventListener('mousemove', this._boundDistort);
            document.addEventListener('mouseup', this._boundDistortEnd);
        }

        _onDistort(e) {
            if (!this._distortDrag) { return; }
            const d = this._distortDrag;
            const screen = { x: d.startScreen.x + (e.clientX - d.startMouse.x), y: d.startScreen.y + (e.clientY - d.startMouse.y) };
            const unrot = rotatePoint(screen, d.center, -(this._rotation || 0));   // in den unrotierten Raum zurück
            const proj = this.getProjection();
            this._corners[d.key] = proj.fromDivPixelToLatLng(new google.maps.Point(unrot.x, unrot.y));
            this._distorted = true;   // Quad ist jetzt kein Rechteck mehr → Kanten-Griffe aus
            this.draw();
            this._emitChange();
        }

        _endDistort() {
            const was = !!this._distortDrag;
            if (this._distortDrag) { this._distortDrag = null; }
            document.removeEventListener('mousemove', this._boundDistort);
            document.removeEventListener('mouseup', this._boundDistortEnd);
            if (this._map) { this._map.setOptions({ draggable: true }); }
            if (was) { this._pushHistory(); }   // Chef 17: Verzerr-Schritt
        }

        // --- Kanten-Griffe (N/O/S/W): einzelne Breite/Höhe; nur bei unverzerrter (rechteckiger) Form ---

        _beginEdge(e, edge) {
            if (this._onSelect) { this._onSelect(); }
            e.preventDefault();
            e.stopPropagation();
            const cp = this._cornerPixels();
            if (!cp) { return; }
            this._edgeDrag = { edge: edge, up: cp.up, startMouse: { x: e.clientX, y: e.clientY } };
            this._map.setOptions({ draggable: false });
            document.addEventListener('mousemove', this._boundEdge);
            document.addEventListener('mouseup', this._boundEdgeEnd);
        }

        _onEdge(e) {
            if (!this._edgeDrag) { return; }
            const ed = this._edgeDrag, up = ed.up, MIN = 8;
            // Maus-Delta in den unrotierten Box-Raum zurückdrehen (Vektor um -rotation):
            const dx = e.clientX - ed.startMouse.x, dy = e.clientY - ed.startMouse.y;
            const rad = -(this._rotation || 0) * Math.PI / 180, cos = Math.cos(rad), sin = Math.sin(rad);
            const dxL = dx * cos - dy * sin, dyL = dx * sin + dy * cos;
            const nw = { x: up.nw.x, y: up.nw.y }, ne = { x: up.ne.x, y: up.ne.y };
            const se = { x: up.se.x, y: up.se.y }, sw = { x: up.sw.x, y: up.sw.y };
            if (ed.edge === 'n') { const y = Math.min(up.sw.y - MIN, up.nw.y + dyL); nw.y = y; ne.y = y; }
            if (ed.edge === 's') { const y = Math.max(up.nw.y + MIN, up.sw.y + dyL); sw.y = y; se.y = y; }
            if (ed.edge === 'w') { const x = Math.min(up.ne.x - MIN, up.nw.x + dxL); nw.x = x; sw.x = x; }
            if (ed.edge === 'e') { const x = Math.max(up.nw.x + MIN, up.ne.x + dxL); ne.x = x; se.x = x; }
            const proj = this.getProjection();
            this._corners = {
                nw: proj.fromDivPixelToLatLng(new google.maps.Point(nw.x, nw.y)),
                ne: proj.fromDivPixelToLatLng(new google.maps.Point(ne.x, ne.y)),
                se: proj.fromDivPixelToLatLng(new google.maps.Point(se.x, se.y)),
                sw: proj.fromDivPixelToLatLng(new google.maps.Point(sw.x, sw.y))
            };
            this.draw();
            this._emitChange();
        }

        _endEdge() {
            const was = !!this._edgeDrag;
            if (this._edgeDrag) { this._edgeDrag = null; }
            document.removeEventListener('mousemove', this._boundEdge);
            document.removeEventListener('mouseup', this._boundEdgeEnd);
            if (this._map) { this._map.setOptions({ draggable: true }); }
            if (was) { this._pushHistory(); }   // Chef 17
        }

        // Kanten-Griffe nur im Resize-Modus, ohne Verzerren-Editiermodus und nur bei rechteckiger Form anzeigen.
        _updateEdgeVisibility() {
            const show = (this._mode === 'resize' && !this._distortEdit && !this._distorted);
            for (const h of (this._edges || [])) {
                h.style.display = show ? '' : 'none';
                h.style.opacity = this._active ? '1' : '0.35';
            }
        }

        // --- Modus / öffentliche Setter ---

        _setMode(mode) {
            this._mode = mode;
            this._applyMode();
            this._emitModeChange();
        }

        // Griffe spiegeln den Modus: Verzerren = türkis + Move-Cursor; rotate = rund + Rotations-Cursor; resize = pink + Resize-Cursor.
        _applyMode() {
            const distort = this._distortEdit;
            const rotate = !distort && this._mode === 'rotate';
            for (let i = 0; i < this._handles.length; i++) {
                const h = this._handles[i];
                if (distort) {
                    h.style.background = '#0aa';
                    h.style.cursor = 'move';
                    h.style.borderRadius = '0';
                } else {
                    h.style.background = 'var(--tc-main, #c356a0)';
                    h.style.cursor = rotate ? ROTATE_CURSOR : HANDLE_CURSOR[HANDLE_KEYS[i]];
                    h.style.borderRadius = rotate ? '50%' : '0';
                }
            }
            this._updateEdgeVisibility();
        }

        setActive(active) {
            this._active = !!active;
            this._applyActive();
        }

        setAspectLock(locked) {
            this._aspectLock = !!locked;   // im Quad-Modell aktuell ohne Wirkung (Scale ist gleichmäßig)
        }

        // Checkbox „verzerren" = Editiermodus an/aus. Nicht-exklusiv: die Verzerrung (Quad) bleibt erhalten.
        setDistort(on) {
            this._distortEdit = !!on;
            this._applyMode();
            this._emitModeChange();
        }

        // Vereinheitlichter Modus für externe Steuerung (z.B. Karten-Toolbar): 'scale' (resize) | 'rotate' | 'skew' (verzerren).
        // Spiegelt die heutige interne (_mode + _distortEdit)-Darstellung, ändert deren Verhalten nicht.
        getMode() {
            return this._distortEdit ? 'skew' : (this._mode === 'rotate' ? 'rotate' : 'scale');
        }
        setMode(mode) {
            if (mode === 'skew') {
                this._distortEdit = true;
            } else {
                this._distortEdit = false;
                this._mode = (mode === 'rotate') ? 'rotate' : 'resize';
            }
            this._applyMode();
            this._emitModeChange();
        }
        _emitModeChange() {
            if (this._onModeChange) { this._onModeChange(this.getMode()); }
        }

        setRotation(deg) {
            this._rotation = Math.round((deg || 0) * 10) / 10;
            this.draw();
            if (this._onRotateChange) { this._onRotateChange(this._rotation); }
        }

        // Verschiebt das ganze Quad um (dx, dy) Pixel (Tastatur-Nudge).
        nudge(dx, dy) {
            const proj = this.getProjection();
            if (!proj) { return; }
            const next = {};
            for (const k of HANDLE_KEYS) {
                const p = proj.fromLatLngToDivPixel(this._corners[k]);
                next[k] = proj.fromDivPixelToLatLng(new google.maps.Point(p.x + dx, p.y + dy));
            }
            this._corners = next;
            this.draw();
            this._emitChange();
        }

        getRotation() {
            return this._rotation;
        }

        // Setzt NUR die Verzerrung zurück (Quad → achsenparallele Box, Größe/Position bleibt); Rotation bleibt.
        reset() {
            this._initCornersFromBounds(this.getBounds());
            this._distortEdit = false;
            this._applyMode();
            this.draw();
            this._emitChange();
            this._emitModeChange();
            this._pushHistory();   // Chef 17: als History-Schritt erfassen
        }

        // --- History (Chef 17): Array von Form-Snapshots + Zeiger; Undo/Redo = Zeiger ±1 (kein Pop, Redo bleibt möglich).
        // Bleibt über die Editiersitzung; beim Speichern wird die Basis neu gesetzt (resetHistory). ---

        _snapshot() {
            const c = {};
            for (const k of HANDLE_KEYS) { c[k] = { lat: this._corners[k].lat(), lng: this._corners[k].lng() }; }
            return { corners: c, rotation: this._rotation || 0, distorted: this._distorted };
        }

        _restoreSnapshot(snap) {
            this._corners = {
                nw: new google.maps.LatLng(snap.corners.nw.lat, snap.corners.nw.lng),
                ne: new google.maps.LatLng(snap.corners.ne.lat, snap.corners.ne.lng),
                se: new google.maps.LatLng(snap.corners.se.lat, snap.corners.se.lng),
                sw: new google.maps.LatLng(snap.corners.sw.lat, snap.corners.sw.lng)
            };
            this._rotation = snap.rotation;
            this._distorted = snap.distorted;
            this.draw();
            this._emitChange();
            if (this._onRotateChange) { this._onRotateChange(this._rotation); }
        }

        // Neuen Snapshot anlegen: ab dem Zeiger abschneiden (Vorwärts-Zweig verwerfen), anhängen, Zeiger ans Ende.
        _pushHistory() {
            this._history = this._history.slice(0, this._historyIdx + 1);
            this._history.push(this._snapshot());
            this._historyIdx = this._history.length - 1;
            this._emitHistoryChange();
        }

        undo() {
            if (this._historyIdx <= 0) { return; }
            this._historyIdx--;
            this._restoreSnapshot(this._history[this._historyIdx]);
            this._emitHistoryChange();
        }

        redo() {
            if (this._historyIdx >= this._history.length - 1) { return; }
            this._historyIdx++;
            this._restoreSnapshot(this._history[this._historyIdx]);
            this._emitHistoryChange();
        }

        canUndo() { return this._historyIdx > 0; }
        canRedo() { return this._historyIdx < this._history.length - 1; }
        // Anzeige zählt ÄNDERUNGEN, nicht Zustände: Position = angewandte Änderungen ab Basis (0 = unverändert),
        // Länge = Anzahl Änderungen OHNE den Basis-Snapshot. Fresh → 0 / 0.
        getHistoryPos() { return this._historyIdx; }
        getHistoryLen() { return Math.max(0, this._history.length - 1); }
        _emitHistoryChange() {
            if (this._onHistoryChange) { this._onHistoryChange(this.canUndo(), this.canRedo(), this.getHistoryPos(), this.getHistoryLen()); }
        }

        // Beim Speichern: aktuelle Form = neue Basis (Array auf einen Snapshot zusammenfalten).
        resetHistory() {
            this._history = [this._snapshot()];
            this._historyIdx = 0;
            this._emitHistoryChange();
        }

        // Chef 17: NUR die Verzerrung löschen (Quad → achsenparallele Box, Größe/Position bleibt), aber IM Skew-Modus
        // BLEIBEN (anders als reset(), das den Editiermodus verlässt). Als History-Schritt erfasst.
        clearDistortion() {
            this._initCornersFromBounds(this.getBounds());
            this._applyMode();
            this.draw();
            this._emitChange();
            this._pushHistory();
        }

        // Experimentelle, in-memory Felder fürs Save-Param-Objekt (Server-Whitelist ignoriert sie bis zur DB-Spalte).
        getParams() {
            const p = { rotation: this._rotation, distorted: this._distorted, distortCorners: {} };
            for (const k of HANDLE_KEYS) { p.distortCorners[k] = { lat: this._corners[k].lat(), lng: this._corners[k].lng() }; }
            return p;
        }

        // Layer-/Stapel-Reihenfolge (vom Host aus der Listenreihenfolge gesetzt). Aktiv bleibt via _applyActive vorn.
        setZIndex(n) {
            this._zIndex = n;
            this._applyActive();
        }

        _applyActive() {
            // z-index am Div: aktiv ganz vorn (klick-/ziehbar bei Überlappung), sonst nach Layer-Reihenfolge (_zIndex).
            if (this._div) { this._div.style.zIndex = String(this._active ? ACTIVE_Z_INDEX : this._zIndex); }
            // Rahmen aufs BILD → warpt mit Rotation/matrix3d, zeigt die Umrisse des gewarpten Objekts.
            if (this._img) {
                this._img.style.outline = this._active ? '2px solid #c356a0' : '1px solid rgba(195,86,160,0.45)';
            }
            const op = this._active ? '1' : '0.35';
            for (const h of this._handles) { h.style.opacity = op; }
            this._updateEdgeVisibility();
        }

        // --- ProjectedOverlay-kompatible API ---

        change(bounds) { this._initCornersFromBounds(bounds); this.draw(); }
        setOpacity(pct) {
            this._opacityPct = Math.max(0, Math.min(100, pct));
            if (this._img) { this._img.style.opacity = String(this._opacityPct / 100); }
        }
        getOpacity() { return this._opacityPct; }   // Chef 19: für den Deckkraft-Slider in der Toolbar
        // Shown/hidden on the editor map only — the eye in the overlay list, not saved (plan
        // bugfix_2026-10-09_polishing-patch-2, phase overlay-auge). Hidden also takes no clicks (handles live in the div).
        setHidden(hidden) {
            this._hidden = !!hidden;
            if (this._div) { this._div.style.visibility = this._hidden ? 'hidden' : ''; }
        }
        isHidden() { return !!this._hidden; }
        remove() { this.setMap(null); }
    };
}

const OPPOSITE = { nw: 'se', ne: 'sw', se: 'nw', sw: 'ne' };
